import { useState } from 'react';
import { apiFetch } from '../../utils/api';
import ProjectCategories from '../../constants/projectCategories';
import '../../styles/admin/AddProjects.css';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';
import FileUpload from '../../components/FileUpload';

// All API calls use apiFetch; no direct fetch or BACKEND_URL

// Use ProjectCategories.OPTIONS for category choices

export default function AddProject() {
  const [form, setForm] = useState({
    name: '',
    displayName: '',
    image: '',
    category: '',
    introduction: '',
    githubUrl: '',
    liveUrl: '',
    pdfUrl: '',
    sections: []
  });
  const [message, setMessage] = useState('');
  
  const handleChange = e => {
    const { name, value } = e.target;
    setForm(form => ({
      ...form,
      [name]: name === "name" ? value.toLowerCase() : value
    }));
  };
  
  const handleSectionChange = (index, field, value) => {
    setForm(form => ({
      ...form,
      sections: form.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
    )
  }));
};

const handleSectionImageChange = (sectionIndex, imageIndex, value) => {
  setForm(form => ({
    ...form,
    sections: form.sections.map((section, i) => 
      i === sectionIndex ? {
      ...section,
      images: section.images.map((img, j) => j === imageIndex ? value : img)
    } : section
  )
}));
};

const addSection = () => {
  if (form.sections.length < 10) { // Limit to 10 sections
    setForm(form => ({
      ...form,
      sections: [...form.sections, {
        subtitle: '',
        body: '',
        images: ['']
      }]
    }));
  }
};

const removeSection = (index) => {
  setForm(form => ({
    ...form,
    sections: form.sections.filter((_, i) => i !== index)
  }));
};

const addImageToSection = (sectionIndex) => {
  const section = form.sections[sectionIndex];
  if (section.images.length < 3) { // Limit to 3 images per section
    setForm(form => ({
      ...form,
      sections: form.sections.map((section, i) => 
        i === sectionIndex ? {
        ...section,
        images: [...section.images, '']
      } : section
    )
  }));
}
};

const removeImageFromSection = (sectionIndex, imageIndex) => {
  setForm(form => ({
    ...form,
    sections: form.sections.map((section, i) => 
      i === sectionIndex ? {
      ...section,
      images: section.images.filter((_, j) => j !== imageIndex)
    } : section
  )
}));
};

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validate sections
  for (let i = 0; i < form.sections.length; i++) {
    const section = form.sections[i];
    if (!section.body.trim()) {
      setMessage(`Content Block ${i + 1} is missing paragraph content`);
      return;
    }
    // Validate image URLs
    for (let j = 0; j < section.images.length; j++) {
      const img = section.images[j];
      if (img && !img.match(/^https?:\/\/.+/)) {
        setMessage(`Content Block ${i + 1}, Image ${j + 1} must be a valid URL`);
        return;
      }
    }
  }
  
  // Validate optional URLs
  if (form.githubUrl && !form.githubUrl.match(/^https?:\/\/.+/)) {
    setMessage('GitHub URL must be a valid URL');
    return;
  }
  if (form.liveUrl && !form.liveUrl.match(/^https?:\/\/.+/)) {
    setMessage('Live URL must be a valid URL');
    return;
  }
  if (form.pdfUrl && !form.pdfUrl.match(/^https?:\/\/.+/)) {
    setMessage('PDF URL must be a valid URL');
    return;
  }
  
  const projectData = {
    name: form.name,
    displayName: form.displayName,
    image: form.image,
    category: form.category,
    introduction: form.introduction,
    githubUrl: form.githubUrl,
    liveUrl: form.liveUrl,
    pdfUrl: form.pdfUrl,
    sections: form.sections.map((section) => ({
      subtitle: section.subtitle,
      body: section.body,
      images: section.images.filter((img) => img.trim() !== ''), // Remove empty image URLs
    })),
  };
  
  try {
    const data = await apiFetch('/api/projects', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(projectData),
    });
    
    console.log('✅ Server response:', data);
    setMessage('Project added successfully!');
    
    setForm({
      name: '',
      displayName: '',
      image: '',
      category: '',
      introduction: '',
      githubUrl: '',
      liveUrl: '',
      pdfUrl: '',
      sections: [],
    });
  } catch (err) {
    console.error('❌ Error adding project:', err);
    setMessage(`Error adding project: ${err.message}`);
  }
};


return (
  <ProtectedAdminRoute>
  <div className="add-project-flex">
  <div className="add-project-form">
  <h2>Add Project</h2>
  <form onSubmit={handleSubmit} autoComplete="off">
  <div className="form-group">
  <label htmlFor="name">Project Name (Database ID)</label>
  <input
  id="name"
  name="name"
  placeholder="project-name-lowercase"
  value={form.name}
  onChange={handleChange}
  required
  autoComplete="off"
  />
  </div>
  
  <div className="form-group">
  <label htmlFor="displayName">Display Name</label>
  <input
  id="displayName"
  name="displayName"
  placeholder="Project Display Name"
  value={form.displayName}
  onChange={handleChange}
  required
  autoComplete="off"
  />
  </div>
  
  <div className="form-group">
  <FileUpload
  label="Display Project Image"
  currentUrl={form.image}
  onUpload={(url) => setForm({...form, image: url})}
  accept="image/*"
  showPreview={true}
  />
  </div>
  
  <div className="form-group">
  <label htmlFor="category">Category</label>
  <select
  id="category"
  name="category"
  className="add-project-select"
  value={form.category}
  onChange={handleChange}
  required
  autoComplete="off"
  >
  <option value="" disabled>Select category</option>
  {ProjectCategories.OPTIONS.map(cat => (
    <option key={cat} value={cat}>{cat}</option>
  ))}
  </select>
  </div>
  
  <div className="form-group">
  <label htmlFor="introduction">Introduction</label>
  <textarea
  id="introduction"
  name="introduction"
  placeholder="Brief introduction or summary of the project..."
  value={form.introduction}
  onChange={handleChange}
  rows={3}
  className="form-textarea"
  required
  autoComplete="off"
  maxLength={500}
  />
  <div className={`character-count ${
    form.introduction.length > 450 ? 'error' : 
    form.introduction.length > 400 ? 'warning' : ''
    }`}>
    {form.introduction.length}/500 characters
    </div>
    </div>
    
    <div className="form-group">
    <label htmlFor="githubUrl">GitHub URL (Optional)</label>
    <input
    id="githubUrl"
    name="githubUrl"
    placeholder="https://github.com/username/repository"
    value={form.githubUrl || ''}
    onChange={handleChange}
    type="url"
    pattern="https?://.+"
    autoComplete="off"
    />
    </div>
    
    <div className="form-group">
    <label htmlFor="liveUrl">Live Demo URL (Optional)</label>
    <input
    id="liveUrl"
    name="liveUrl"
    placeholder="https://your-project-demo.com"
    value={form.liveUrl || ''}
    onChange={handleChange}
    type="url"
    pattern="https?://.+"
    autoComplete="off"
    />
    </div>
    
    <div className="form-group">
    <FileUpload
    label="Project PDF Document (Optional)"
    currentUrl={form.pdfUrl}
    onUpload={(url) => setForm({...form, pdfUrl: url})}
    accept="application/pdf"
    showPreview={true}
    />
    </div>
    
    {/* Dynamic Sections */}
    <div className="sections-container">
    <div className="sections-header">
    <h3>Article Content</h3>
    </div>
    
    {form.sections.map((section, sectionIndex) => (
      <div key={sectionIndex} className="project-section">
      <div className="section-header">
      <h4>Content Block {sectionIndex + 1}</h4>
      <button 
      type="button" 
      onClick={() => removeSection(sectionIndex)}
      className="remove-section-btn"
      >
      Remove
      </button>
      </div>
      
      <div className="form-group">
      <label>Heading (Optional)</label>
      <input
      type="text"
      placeholder="Section heading or subheading..."
      value={section.subtitle}
      onChange={(e) => handleSectionChange(sectionIndex, 'subtitle', e.target.value)}
      />
      </div>
      
      <div className="form-group">
      <label>Paragraph Content</label>
      <textarea
      placeholder="Write your article content here. Each line will become a separate paragraph..."
      value={section.body}
      onChange={(e) => handleSectionChange(sectionIndex, 'body', e.target.value)}
      rows={6}
      className="form-textarea"
      required
      />
      </div>
      
      <div className="section-images">
      <div className="images-header">
      <label>Images (Optional)</label>
      <button 
      type="button" 
      onClick={() => addImageToSection(sectionIndex)}
      className="add-image-btn"
      disabled={section.images.length >= 3}
      >
      + Add Image
      </button>
      </div>
      
      {section.images.map((image, imageIndex) => (
        <div key={imageIndex} className="image-input-group">
        <FileUpload
        label={`Image ${imageIndex + 1}`}
        currentUrl={image}
        onUpload={(url) => handleSectionImageChange(sectionIndex, imageIndex, url)}
        accept="image/*"
        showPreview={true}
        />
        {section.images.length > 1 && (
          <button 
          type="button" 
          onClick={() => removeImageFromSection(sectionIndex, imageIndex)}
          className="remove-image-btn"
          >
          Remove
          </button>
        )}
        </div>
      ))}
      </div>
      </div>
    ))}
    
    <button 
    type="button" 
    onClick={addSection}
    className="add-section-btn"
    disabled={form.sections.length >= 10}
    >
    + Add Content Block
    </button>
    </div>
    
    <button type="submit" className="submit-btn">Add Project</button>
    </form>
    
    {message && (
      <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
      {message}
      </div>
    )}
    </div>
    </div>
    </ProtectedAdminRoute>
  );
}